import {
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { useLocation } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { message } from "antd";
import customFetch, { HTTP_STATUS_KEY } from "services/api";
import { getSearchStateFromLocation } from "hooks/update-search";
import { notifyWriteSaved } from "utils/saved-writes";

/**
 * Extract the base resource from a URL for targeted query invalidation.
 * e.g. "dashboard/invoices/sales/123/edit" → "dashboard/invoices"
 * This ensures mutations only invalidate related queries, not the entire cache.
 */
const getResourceKey = (url) => {
  if (!url) return undefined;
  const base = url.split("?")[0];
  const parts = base.split("/").filter(Boolean);
  // Use first two segments as the resource identifier
  return parts.slice(0, 2).join("/");
};

const buildQueryString = (search) => {
  const params = new URLSearchParams();
  Object.keys(search || {}).forEach((key) => {
    const value = search[key];
    if (value == null || value === "") return;
    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item != null && item !== "") {
          params.append(key, item);
        }
      });
    } else {
      params.append(key, value);
    }
  });
  return params.toString();
};

const DEFAULT_PAGINATION = { current: 1, pageSize: 15, total: 0 };

const hasPaginationShape = (value) =>
  value &&
  typeof value === "object" &&
  !Array.isArray(value) &&
  ("current_page" in value ||
    "per_page" in value ||
    "total" in value ||
    "last_page" in value);

const toUiPagination = (rawPagination, fallbackTotal = 0) => {
  if (!hasPaginationShape(rawPagination)) {
    return {
      current: 1,
      pageSize: fallbackTotal > 0 ? fallbackTotal : 15,
      total: fallbackTotal,
    };
  }

  const current = Number(rawPagination.current_page) || 1;
  const pageSize = Number(rawPagination.per_page) || 15;
  const total = Number(rawPagination.total);

  return {
    current,
    pageSize,
    total: Number.isFinite(total) ? total : fallbackTotal,
  };
};

const extractNormalizedPayload = (rawResponse) => {
  const payload = rawResponse?.data;
  const {
    status: _status,
    message: _message,
    data: _data,
    pagination: _pagination,
    errors: _errors,
    ...rootExtras
  } = rawResponse || {};

  if (Array.isArray(payload)) {
    return {
      data: payload,
      pagination: rawResponse?.pagination,
      extras: rootExtras,
    };
  }

  if (payload && typeof payload === "object" && !Array.isArray(payload)) {
    if (Array.isArray(payload.data)) {
      const {
        data: rows,
        pagination,
        meta,
        current_page,
        per_page,
        total,
        ...payloadExtras
      } = payload;

      return {
        data: rows,
        pagination: rawResponse?.pagination ||
          pagination ||
          meta || {
            current_page,
            per_page,
            total,
          },
        extras: { ...payloadExtras, ...rootExtras },
      };
    }

    return {
      data: payload,
      pagination: rawResponse?.pagination,
      extras: rootExtras,
    };
  }

  return {
    data: payload ?? null,
    pagination: rawResponse?.pagination,
    extras: rootExtras,
  };
};

/**
 * React Query wrapper for data fetching - drop-in replacement for useFetch
 * Returns the same shape: { data, fetchLoading, error, pagination, total, setData, refetch }
 */
export const useApiQuery = (
  url,
  page = null,
  hasSearchParams = false,
  otherParam = null,
  fetchOptions = {},
  extraOptions = {},
  searchExcludeParams
) => {
  const location = useLocation();
  const [localData, setLocalData] = useState(null);

  useEffect(() => {
    setLocalData(null);
  }, [url]);

  const searchStateFromUrl = hasSearchParams
    ? getSearchStateFromLocation(location.search, searchExcludeParams)
    : null;
  const effectivePage =
    hasSearchParams && searchStateFromUrl?.hasPageInUrl
      ? searchStateFromUrl.page
      : page;
  const effectiveSearchParam =
    hasSearchParams && searchStateFromUrl
      ? searchStateFromUrl.searchParams
      : null;

  const queryKey = useMemo(
    () =>
      [url, effectivePage, effectiveSearchParam, otherParam].filter(Boolean),
    [url, effectivePage, effectiveSearchParam, otherParam]
  );

  const queryFn = async () => {
    if (!url) return null;

    const searchParams = effectiveSearchParam
      ? buildQueryString(effectiveSearchParam)
      : "";
    const pageParam = effectivePage ? `page=${effectivePage}` : "";
    const othersParams = otherParam ? buildQueryString(otherParam) : "";

    const queryParts = [pageParam, searchParams, othersParams].filter(Boolean);
    const queryString = queryParts.length ? `?${queryParts.join("&")}` : "";
    const finalUrl = `${url}${queryString}`;

    const response = await customFetch(finalUrl, fetchOptions);
    return response;
  };

  const {
    data: rawResponse,
    isLoading,
    isFetching,
    isSuccess,
    isPlaceholderData,
    error,
    refetch,
  } = useQuery({
    queryKey,
    queryFn,
    enabled: !!url,
    ...extraOptions,
  });

  const processed = useMemo(() => {
    if (!rawResponse) {
      return {
        data: localData,
        pagination: DEFAULT_PAGINATION,
        total: 0,
      };
    }

    if (!rawResponse?.status) {
      return {
        data: localData,
        pagination: DEFAULT_PAGINATION,
        total: 0,
      };
    }

    const {
      data: normalizedData,
      pagination,
      extras,
    } = extractNormalizedPayload(rawResponse);
    const fallbackTotal = Array.isArray(normalizedData)
      ? normalizedData.length
      : 0;
    const normalizedPagination = toUiPagination(pagination, fallbackTotal);

    return {
      ...extras,
      data: localData !== null ? localData : normalizedData,
      pagination: normalizedPagination,
      total: normalizedPagination.total,
    };
  }, [rawResponse, localData]);

  const setData = (updater) => {
    if (typeof updater === "function") {
      setLocalData((prev) => updater(prev ?? processed.data));
    } else {
      setLocalData(updater);
    }
  };

  return {
    ...processed,
    data: processed.data,
    fetchLoading: isLoading,
    isFetching,
    isSuccess,
    isPlaceholderData,
    error: error?.message || null,
    // The HTTP status of an error envelope, which `customFetch` resolves rather
    // than throwing — so React Query's own `error` stays null and only these
    // tell a missing record apart from an endpoint that answered nothing.
    httpStatus: rawResponse?.[HTTP_STATUS_KEY] ?? null,
    notFound: rawResponse?.[HTTP_STATUS_KEY] === 404,
    pagination: processed.pagination,
    total: processed.total,
    setData,
    refetch,
    // What this query is reading, ready to hand to a table's Excel export
    // (`<PaginatedListContainer exportUrl={exportUrl} exportParams={exportParams} />`).
    // The export is the same endpoint plus `?export=xlsx`, so passing them from here is what
    // keeps the file and the table agreeing: a page that typed its export URL out separately
    // would keep exporting the old endpoint after the list moved, and `otherParam` — the
    // scope that makes this list *this* list, e.g. `{ type: "purchase" }` — is exactly the
    // thing a hand-written export forgets, silently exporting every type.
    exportUrl: url,
    exportParams: otherParam,
  };
};

/**
 * Infinite / "load more" data fetching for paginated endpoints.
 *
 * Wraps TanStack `useInfiniteQuery` over `customFetch`, deriving the next page
 * from the unified response envelope's `pagination` meta (current_page /
 * last_page / has_more_pages). Pages are flattened into a single cumulative
 * `data` array, so dropdowns can append options as the user scrolls instead of
 * being capped at a single page.
 *
 * @param {string|null} url - API path (null disables the query)
 * @param {object} opts
 * @param {object|null} opts.searchParams - extra query params (search term, filters, id, …)
 * @param {number} opts.perPage - page size (default 15)
 * @param {boolean} opts.enabled - gate the query
 * @param {object} opts.fetchOptions - passed to customFetch (e.g. { baseUrl: "central" })
 * @param {object} opts.extraOptions - extra react-query options
 * @returns {{ data, total, fetchLoading, isFetchingNextPage, hasNextPage, fetchNextPage, refetch, error }}
 */
export const useInfiniteApiQuery = (
  url,
  {
    searchParams = null,
    perPage = 15,
    enabled = true,
    fetchOptions = {},
    extraOptions = {},
  } = {}
) => {
  const queryKey = useMemo(
    () => ["infinite", url, searchParams, perPage],
    [url, searchParams, perPage]
  );

  const query = useInfiniteQuery({
    queryKey,
    enabled: !!url && enabled,
    initialPageParam: 1,
    queryFn: async ({ pageParam = 1 }) => {
      const merged = {
        ...(searchParams || {}),
        per_page: perPage,
        page: pageParam,
      };
      const queryString = buildQueryString(merged);
      const finalUrl = queryString ? `${url}?${queryString}` : url;
      return customFetch(finalUrl, fetchOptions);
    },
    getNextPageParam: (lastPage) => {
      const pagination = lastPage?.pagination;
      if (!pagination || typeof pagination !== "object") return undefined;
      const current = Number(pagination.current_page) || 1;
      if (pagination.has_more_pages === true) return current + 1;
      if (pagination.has_more_pages === false) return undefined;
      const last = Number(pagination.last_page) || current;
      return current < last ? current + 1 : undefined;
    },
    ...extraOptions,
  });

  const data = useMemo(() => {
    const pages = query.data?.pages || [];
    return pages.flatMap((response) =>
      Array.isArray(response?.data) ? response.data : []
    );
  }, [query.data]);

  const pages = query.data?.pages || [];
  const lastPagination = pages[pages.length - 1]?.pagination;
  const total =
    lastPagination && typeof lastPagination === "object"
      ? Number(lastPagination.total) || data.length
      : data.length;

  return {
    data,
    total,
    fetchLoading: query.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: !!query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    refetch: query.refetch,
    error: query.error?.message || null,
  };
};

/**
 * React Query wrapper for mutations - drop-in replacement for useManageRequest
 * Returns the same shape: { create, update, update2/patch, deleteItem, doRequest, loading, successMessage, successOpen, setSuccessOpen }
 */
export const useApiMutation = ({
  onCreate,
  onDelete,
  onUpdate,
  onGet,
  fetchOptions = {},
} = {}) => {
  const queryClient = useQueryClient();
  const [successOpen, setSuccessOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const invalidateRelated = (url, { excludeUrl } = {}) => {
    const resource = getResourceKey(url);
    if (!resource) return;
    // Single invalidation: prefix match on first queryKey segment (e.g. list + *-search URLs).
    // Avoid calling invalidateQueries twice — same queries matched both ways and refetched twice.
    queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey[0];
        if (typeof key !== "string" || !key.startsWith(resource)) return false;
        // A record's `show` query is fetched from the same URL its `delete` posts to
        // (`dashboard/<resource>/{id}` both ways), so it shares this exact key —
        // never refetch it: the record is gone and it can only 404.
        return key !== excludeUrl;
      },
    });
  };

  const mutation = useMutation({
    mutationFn: async ({ url, method, body }) => {
      return customFetch(url, { ...fetchOptions, method, body });
    },
  });

  /**
   * Detects whether the first argument to `create()` / `update()` / `deleteItem()` is the CLAUDE.md object style
   * (`{ url, body, ... }`, `{ url, id, ... }`) or the legacy positional style.
   * Used for backward compatibility — prefer the object style in new code.
   */
  const isMutationPayloadObject = (value) =>
    value != null &&
    typeof value === "object" &&
    !Array.isArray(value) &&
    !(value instanceof FormData) &&
    Object.prototype.hasOwnProperty.call(value, "url");

  const create = async (arg1, arg2, arg3 = null) => {
    let url;
    let formData;
    let customSuccessMessage;
    if (isMutationPayloadObject(arg1)) {
      url = arg1.url;
      formData = arg1.body;
      customSuccessMessage = arg1.customSuccessMessage ?? null;
    } else {
      url = arg1;
      formData = arg2;
      customSuccessMessage = arg3;
    }
    try {
      const response = await mutation.mutateAsync({
        url,
        method: "POST",
        body: formData,
      });
      if (response.status) {
        const apiMessage = response.message || response?.data?.message;
        // Before the callback: `onCreate` is where a form navigates away after saving.
        notifyWriteSaved();
        onCreate && onCreate(response.data, url, apiMessage);
        setSuccessMessage(customSuccessMessage || apiMessage);
        setSuccessOpen(true);
        invalidateRelated(url);
      } else {
        if (Array.isArray(response)) {
          onCreate && onCreate(response, url);
        }
      }
    } catch (error) {
      message.error("Network error: " + error.message);
    }
  };

  const update = async (arg1, arg2, arg3 = null, arg4) => {
    let url;
    let formData;
    let customSuccessMessage;
    let method;
    if (isMutationPayloadObject(arg1)) {
      url = arg1.url;
      formData = arg1.body;
      customSuccessMessage = arg1.customSuccessMessage ?? null;
      method = arg1.method ?? "PUT";
    } else {
      url = arg1;
      formData = arg2;
      customSuccessMessage = arg3;
      method = arg4 ?? "PUT";
    }
    try {
      // FormData over PUT is not supported by many servers (including Laravel).
      // Use POST + _method=PUT (method spoofing) as the wire format.
      // Do NOT change method back to PUT here — it will break multipart uploads.
      if (formData instanceof FormData) {
        formData.append("_method", "PUT");
        method = "POST";
      }
      const response = await mutation.mutateAsync({
        url,
        method,
        body: formData,
      });
      if (response.status) {
        notifyWriteSaved();
        onUpdate && onUpdate(response.data, url);
        setSuccessMessage(customSuccessMessage || response.message);
        setSuccessOpen(true);
        invalidateRelated(url);
      }
    } catch (error) {
      message.error("Network error: " + error.message);
    }
  };

  const update2 = async (url, formData) => {
    try {
      const response = await mutation.mutateAsync({
        url,
        method: "PATCH",
        body: formData,
      });
      if (response.status) {
        notifyWriteSaved();
        onUpdate && onUpdate(response.data, url);
        setSuccessMessage(
          typeof response?.data === "string" && !response?.message
            ? response?.data
            : response?.message
        );
        setSuccessOpen(true);
        invalidateRelated(url);
      }
    } catch (error) {
      message.error("Network error: " + error.message);
    }
  };

  const patch = async (url, formData, customSuccessMessage = null) => {
    try {
      const response = await mutation.mutateAsync({
        url,
        method: "PATCH",
        body: formData,
      });
      if (response.status) {
        notifyWriteSaved();
        onCreate && onCreate(response.data, url);
        setSuccessMessage(customSuccessMessage || response.message);
        setSuccessOpen(true);
        invalidateRelated(url);
      } else {
        if (Array.isArray(response)) {
          onCreate && onCreate(response, url);
        }
      }
    } catch (error) {
      message.error("Network error: " + error.message);
    }
  };

  const deleteItem = async (arg1, arg2, arg3 = {}) => {
    let url;
    let id;
    let skipInvalidate = false;
    let customSuccessMessage = null;

    if (isMutationPayloadObject(arg1)) {
      url = arg1.url;
      id = arg1.id;
      skipInvalidate = arg1.skipInvalidate ?? false;
      customSuccessMessage = arg1.customSuccessMessage ?? null;
    } else {
      url = arg1;
      id = arg2;
      const opts =
        arg3 && typeof arg3 === "object" && !Array.isArray(arg3) ? arg3 : {};
      skipInvalidate = opts.skipInvalidate ?? false;
      customSuccessMessage = opts.customSuccessMessage ?? null;
    }

    try {
      const response = await mutation.mutateAsync({ url, method: "DELETE" });
      if (response && response.status === true) {
        onDelete && onDelete(id);
        setSuccessMessage(
          customSuccessMessage ||
            response.message ||
            response?.data?.message ||
            (typeof response?.data === "string" ? response.data : "")
        );
        setSuccessOpen(true);
        if (!skipInvalidate) {
          // Never revalidate the deleted record's own `show` query — it is
          // fetched from this same URL and can now only 404. List/search/stats
          // queries under the same resource still refresh as usual.
          invalidateRelated(url, { excludeUrl: url });
        }
      }
    } catch (error) {
      message.error("Network error: " + error.message);
    }
  };

  const doRequest = async (
    url,
    navigateUrl,
    method,
    module,
    formData,
    setData,
    id,
    notHasAlert = false
  ) => {
    try {
      const response = await mutation.mutateAsync({
        url,
        method,
        body: formData,
      });
      if (response.status) {
        if (!notHasAlert) message.success(`${module} saved successfully`);
        if (method === "POST") {
          setData &&
            setData((prev) =>
              Array.isArray(prev) ? [...prev, response.data] : response.data
            );
        } else if (method === "PUT") {
          setData &&
            setData((prev) =>
              prev.map((p) => (p.id === id ? response.data : p))
            );
        } else if (method === "DELETE") {
          setData && setData((prev) => prev.filter((p) => p.id !== id));
        }
        setSuccessMessage(response.message);
        setSuccessOpen(true);
        // A DELETE never revalidates its own `show` query (same URL, guaranteed
        // 404 once the record is gone) — see `deleteItem`.
        invalidateRelated(url, method === "DELETE" ? { excludeUrl: url } : {});
      }
    } catch (error) {
      message.error("Network error: " + error.message);
    }
  };

  const getAction = async (url, formData, customSuccessMessage = null) => {
    try {
      const response = await mutation.mutateAsync({
        url,
        method: "GET",
        body: formData,
      });
      if (response.status) {
        onGet && onGet(response.data, url);
        setSuccessMessage(customSuccessMessage || response.message);
        setSuccessOpen(true);
        invalidateRelated(url);
      } else {
        if (Array.isArray(response)) {
          onGet && onGet(response, url);
        }
      }
    } catch (error) {
      message.error("Network error: " + error.message);
    }
  };

  return {
    doRequest,
    loading: mutation.isPending,
    create,
    update,
    update2,
    deleteItem,
    successMessage,
    successOpen,
    setSuccessOpen,
    patch,
    getAction,
  };
};

/**
 * React Query wrapper for delete operations - drop-in replacement for useDelete
 */
export const useApiDelete = (baseUrl, setData) => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (id) => {
      return customFetch(`${baseUrl}/${id}/delete`, { method: "DELETE" });
    },
    onSuccess: (res, id) => {
      const msg = {
        content: "",
        duration: 2,
      };
      if (typeof res?.data === "string" && !res?.message) {
        msg.content = res?.data;
      } else {
        msg.content = res?.message;
      }
      if (res.status) {
        message.success(msg);
        if (setData) {
          setData((prevState) => prevState.filter((item) => item.id !== id));
        }
        const resource = getResourceKey(baseUrl);
        if (resource) {
          // Never revalidate the deleted record's own `show` query
          // (`${baseUrl}/${id}`, not this hook's own `/delete`-suffixed URL) —
          // it can now only 404. See `deleteItem` in `useApiMutation`.
          const showUrl = `${baseUrl}/${id}`;
          queryClient.invalidateQueries({
            predicate: (query) => {
              const key = query.queryKey[0];
              return (
                typeof key === "string" &&
                key.startsWith(resource) &&
                key !== showUrl
              );
            },
          });
        }
      }
    },
  });

  return { deleteItem: (id) => mutation.mutate(id) };
};
